import { stringify, waitAsync } from '@/Functions';
import { localStorageDB } from '../LocalStorage';
import {
    PostType,
    LikeType,
    BookmarkType,
    ReportType
} from './types';
import { Mention } from 'ai-api/types/social';
import { EssayTopic } from './MrMate';
import { SQLite, SQLiteDatabase } from '../WebDatabase';

/**
 * Interface for SQLite cache operations
 * NOTE: SQLite is used as LOCAL CACHE only, NOT as primary database
 * Server/API is the source of truth for all social data
 */
interface ISocialDatabase {
    init(): Promise<void>;
    
    // Posts
    savePosts(posts: PostType[]): Promise<void>;
    getAllPosts(): Promise<PostType[]>;
    getPostsByUser(addressid: string): Promise<PostType[]>;
    deletePost(postid: string): Promise<void>;
    
    
    // Likes
    saveLikes(likes: LikeType[]): Promise<void>;
    getAllLikes(): Promise<LikeType[]>;
    getPostLikes(postid: string): Promise<LikeType[]>;
    getUserLikes(addressid: string): Promise<LikeType[]>;
    deleteLike(addressid: string, postid: string): Promise<void>;
    
    // Bookmarks
    saveBookmarks(bookmarks: BookmarkType[]): Promise<void>;
    getAllBookmarks(): Promise<BookmarkType[]>;
    getUserBookmarks(addressid: string, folder_name?: string): Promise<BookmarkType[]>;
    deleteBookmark(addressid: string, postid: string): Promise<void>;
    
    // Post Hashtags
    savePostHashtags(postid: string, hashtags: string[]): Promise<void>;
    getPostHashtags(postid: string): Promise<string[]>;
    getPostsByHashtag(hashtag: string): Promise<string[]>;
    deletePostHashtags(postid: string): Promise<void>;
    
    
    
    // Reports
    saveReports(reports: ReportType[]): Promise<void>;
    getAllReports(): Promise<ReportType[]>;
    getUserReports(addressid: string): Promise<ReportType[]>;
    
    // Essay Topics (Mr. MATE)
    saveEssayTopics(topics: EssayTopic[]): Promise<void>;
    getEssayTopics(): Promise<EssayTopic[]>;
    
    release(): void;
}

/**
 * SQLite cache service for social operations
 * 
 * IMPORTANT: This is a LOCAL CACHE layer only!
 * - Server/API is the primary source of truth
 * - SQLite stores cached data for offline access and performance
 * - Always sync with server for authoritative data
 * - Cache can be cleared/reset without data loss
 */
class SocialDatabase implements ISocialDatabase {
    private db: SQLiteDatabase | null = null;
    private semaphore: boolean = false;

    private acquireSemaphore = async () => {
        let count = 0;
        while (this.semaphore && count < 100) { await waitAsync(50); count++; }
        if (this.semaphore) { throw new Error('Failed to acquire social semaphore: timeout'); }
        this.semaphore = true;
    }

    private releaseSemaphore = () => this.semaphore = false;
    
    async dropTables(): Promise<void> {
        await this.acquireSemaphore();
        await this.dropTablesUnsafe();
        this.releaseSemaphore();
    }

    private async dropTablesUnsafe(): Promise<void> {
        const now = Math.floor(Date.now() / 1000);
        if (!this.db) this.db = await SQLite.openDatabaseAsync('social.db');
        
        try {
            await this.db.execAsync(`
                DROP TABLE IF EXISTS posts;
                DROP TABLE IF EXISTS likes;
                DROP TABLE IF EXISTS bookmarks;
                DROP TABLE IF EXISTS post_hashtag;
                DROP TABLE IF EXISTS reports;
                DROP TABLE IF EXISTS essay_topics;
            `);
            this.db?.closeSync();
            this.db = null;
        } catch (error) {
            console.error('Failed to drop social tables:', error);
        }
        console.log(`Social tables dropped successfully. time elapsed:${Date.now() / 1000 - now}`);
    }

    /**
     * Initialize cache and create tables if they don't exist
     * NOTE: Creates local cache tables, not primary database
     */
    async init(): Promise<void> {
        if (this.db) { return; }
        await this.acquireSemaphore();
        try {
            await localStorageDB.init();
            const db_version = localStorageDB.getItem('db_version:social');

            const DB_VERSION = '6.0';
            if (db_version && db_version !== DB_VERSION) {
                await this.dropTablesUnsafe();
            }
            await localStorageDB.setItem('db_version:social', DB_VERSION);
            this.db = await SQLite.openDatabaseAsync('social.db');
            
            // Create posts table
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS posts (
                    postid TEXT PRIMARY KEY UNIQUE,
                    addressid TEXT NOT NULL,
                    content TEXT NOT NULL,
                    quoted_postid TEXT,
                    parent_postid TEXT,
                    media TEXT,
                    visibility TEXT NOT NULL DEFAULT 'public',
                    translated TEXT,
                    like_count INTEGER NOT NULL DEFAULT 0,
                    reply_count INTEGER NOT NULL DEFAULT 0,
                    bookmark_count INTEGER NOT NULL DEFAULT 0,
                    view_count INTEGER NOT NULL DEFAULT 0,
                    created_at INTEGER NOT NULL,
                    updated_at INTEGER NOT NULL
                )
            `);
            
            
            // Create likes table
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS likes (
                    addressid TEXT NOT NULL,
                    postid TEXT NOT NULL,
                    created_at INTEGER NOT NULL,
                    PRIMARY KEY (addressid, postid)
                )
            `);
            
            // Create bookmarks table
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS bookmarks (
                    addressid TEXT NOT NULL,
                    postid TEXT NOT NULL,
                    folder_name TEXT NOT NULL DEFAULT 'default',
                    created_at INTEGER NOT NULL,
                    PRIMARY KEY (addressid, postid)
                )
            `);
            
            // Create post_hashtag table for many-to-many relationship
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS post_hashtag (
                    postid TEXT NOT NULL,
                    tag_name TEXT NOT NULL,
                    created_at INTEGER NOT NULL,
                    PRIMARY KEY (postid, tag_name)
                )
            `);
            
            // Create post_hashtag table for many-to-many relationship
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS mentions (
                    postid TEXT NOT NULL,
                    addressid TEXT NOT NULL,
                    position INTEGER NOT NULL,
                    PRIMARY KEY (postid, addressid)
                )
            `);
            
            
            // Create reports table
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS reports (
                    addressid TEXT NOT NULL,
                    target TEXT NOT NULL,
                    reason TEXT NOT NULL,
                    description TEXT NOT NULL DEFAULT '',
                    status TEXT NOT NULL DEFAULT 'pending',
                    created_at INTEGER NOT NULL,
                    resolved_at INTEGER,
                    resolved_by TEXT,
                    PRIMARY KEY (addressid, target)
                )
            `);
            
            // Create essay_topics table for Mr. MATE
            await this.db.execAsync(`
                CREATE TABLE IF NOT EXISTS essay_topics (
                    topicid TEXT PRIMARY KEY UNIQUE,
                    hashtags TEXT NOT NULL,
                    encouraging_kr TEXT NOT NULL,
                    encouraging_id TEXT NOT NULL,
                    created_date TEXT NOT NULL,
                    created_at INTEGER NOT NULL
                )
            `);

            this.releaseSemaphore();
        } catch (error) {
            this.releaseSemaphore();
            console.error('Failed to initialize social database:', error);
            throw error;
        }
    }
    
    // Posts operations
    async savePosts(posts: PostType[]): Promise<void> {
        if (!this.db) await this.init();
        if (!posts.length) return;
        
        try {
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO posts 
            (
                postid, addressid, content, quoted_postid, parent_postid,
                media, visibility, translated, like_count, reply_count, bookmark_count, view_count,
                created_at, updated_at
            )
            VALUES
            ${posts.map(post => `(
                ${stringify(post.postid)},
                ${stringify(post.addressid)},
                ${stringify(post.content)},
                ${post.quoted_postid ? stringify(post.quoted_postid) : 'NULL'},
                ${post.parent_postid ? stringify(post.parent_postid) : 'NULL'},
                ${post.media ? stringify(JSON.stringify(post.media)) : 'NULL'},
                ${stringify(post.visibility || 'public')},
                ${post.translated ? stringify(post.translated) : 'NULL'},
                ${stringify(post.like_count || 0)},
                ${stringify(post.reply_count || 0)},
                ${stringify(post.bookmark_count || 0)},
                ${stringify(post.view_count || 0)},
                ${stringify(post.created_at)},
                ${stringify(post.updated_at)}
            )`).join(',\n')}`);
            
            // Save hashtags for each post
            for (const post of posts) {
                if (post.hashtags && post.hashtags.length > 0) {
                    await this.savePostHashtags(post.postid, post.hashtags);
                }

                if (post.mentions && post.mentions.length > 0) {
                    await this.db!.execAsync(`
                        INSERT OR REPLACE INTO mentions (postid, addressid, position)
                        VALUES
                        ${post.mentions.map(mention => `(
                            ${stringify(post.postid)},
                            ${stringify(mention.addressid)},
                            ${stringify(mention.position)}
                        )`).join(',\n')}`);
                }
            }
            
        } catch (error) {
            console.error('Failed to save posts:', error);
            throw error;
        }
    }
    
    async getAllPosts(): Promise<PostType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<PostType>(`
                SELECT * FROM posts ORDER BY created_at DESC
            `);

            const hashtags = await this.db!.getAllAsync<{ postid: string, tag_name: string }>(`
                SELECT postid, tag_name FROM post_hashtag
                WHERE postid IN (${results.map(post => stringify(post.postid)).join(',')})
            `);
            const hashtagMap: Record<string, string[]> = {};
            hashtags.forEach(row => {
                if (!hashtagMap[row.postid]) {
                    hashtagMap[row.postid] = [];
                }
                hashtagMap[row.postid].push(row.tag_name);
            });

            const mentionedUsers = await this.db!.getAllAsync<{ postid: string, addressid: string, position: number }>(`
                SELECT postid, addressid, position FROM mentions
                WHERE postid IN (${results.map(post => stringify(post.postid)).join(',')})
            `);
            const mentionMap: Record<string, Mention[]> = {};
            mentionedUsers.forEach(({postid, addressid, position}) => {
                if (!mentionMap[postid]) {
                    mentionMap[postid] = [];
                }
                mentionMap[postid].push({addressid, position});
            });

            results.forEach(post => {
                post.hashtags = hashtagMap[post.postid] || [];
                post.mentions = mentionMap[post.postid] || [];
                // Parse media JSON string back to array
                if (post.media && typeof post.media === 'string') {
                    try {
                        post.media = JSON.parse(post.media);
                    } catch {
                        post.media = [];
                    }
                }
            });

            return results;
        } catch (error) {
            console.error('Failed to get all posts:', error);
            return [];
        }
    }
    
    async getPostsByUser(addressid: string): Promise<PostType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<PostType>(`
                SELECT * FROM posts WHERE addressid = ${stringify(addressid)} ORDER BY created_at DESC
            `);
            
            // Parse media JSON string back to array
            results.forEach(post => {
                if (post.media && typeof post.media === 'string') {
                    try {
                        post.media = JSON.parse(post.media);
                    } catch {
                        post.media = [];
                    }
                }
            });
            
            return results;
        } catch (error) {
            console.error(`Failed to get posts for user ${addressid}:`, error);
            return [];
        }
    }
    
    async deletePost(postid: string): Promise<void> {
        if (!this.db) await this.init();
        
        try {
            // Delete post hashtags first
            await this.deletePostHashtags(postid);
            
            // Delete the post
            await this.db!.execAsync(`
                DELETE FROM posts WHERE postid = ${stringify(postid)}
            `);
        } catch (error) {
            console.error(`Failed to delete post ${postid}:`, error);
            throw error;
        }
    }
    
    
    // Likes operations
    async saveLikes(likes: LikeType[]): Promise<void> {
        if (!this.db) await this.init();
        if (!likes.length) return;
        
        try {
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO likes (addressid, postid, created_at)
            VALUES
            ${likes.map(like => `(
                ${stringify(like.addressid)},
                ${stringify(like.postid)},
                ${stringify(like.created_at)}
            )`).join(',\n')}`);
        } catch (error) {
            console.error('Failed to save likes:', error);
            throw error;
        }
    }
    
    async getAllLikes(): Promise<LikeType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<LikeType>(`
                SELECT * FROM likes ORDER BY created_at DESC
            `);
            return results;
        } catch (error) {
            console.error('Failed to get all likes:', error);
            return [];
        }
    }
    
    async getPostLikes(postid: string): Promise<LikeType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<LikeType>(`
                SELECT * FROM likes WHERE postid = ${stringify(postid)} ORDER BY created_at DESC
            `);
            return results;
        } catch (error) {
            console.error(`Failed to get likes for post ${postid}:`, error);
            return [];
        }
    }
    
    async getUserLikes(addressid: string): Promise<LikeType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<LikeType>(`
                SELECT * FROM likes WHERE addressid = ${stringify(addressid)} ORDER BY created_at DESC
            `);
            return results;
        } catch (error) {
            console.error(`Failed to get likes for user ${addressid}:`, error);
            return [];
        }
    }
    
    async deleteLike(addressid: string, postid: string): Promise<void> {
        if (!this.db) await this.init();
        
        try {
            await this.db!.execAsync(`
                DELETE FROM likes WHERE addressid = ${stringify(addressid)} AND postid = ${stringify(postid)}
            `);
        } catch (error) {
            console.error(`Failed to delete like ${addressid} -> ${postid}:`, error);
            throw error;
        }
    }
    
    // Bookmarks operations
    async saveBookmarks(bookmarks: BookmarkType[]): Promise<void> {
        if (!this.db) await this.init();
        if (!bookmarks.length) return;
        
        try {
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO bookmarks (addressid, postid, folder_name, created_at)
            VALUES
            ${bookmarks.map(bookmark => `(
                ${stringify(bookmark.addressid)},
                ${stringify(bookmark.postid)},
                ${stringify(bookmark.folder_name || 'default')},
                ${stringify(bookmark.created_at)}
            )`).join(',\n')}`);
        } catch (error) {
            console.error('Failed to save bookmarks:', error);
            throw error;
        }
    }
    
    async getAllBookmarks(): Promise<BookmarkType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<BookmarkType>(`
                SELECT * FROM bookmarks ORDER BY created_at DESC
            `);
            return results;
        } catch (error) {
            console.error('Failed to get all bookmarks:', error);
            return [];
        }
    }
    
    async getUserBookmarks(addressid: string, folder_name?: string): Promise<BookmarkType[]> {
        if (!this.db) await this.init();
        
        try {
            const query = folder_name 
                ? `SELECT * FROM bookmarks WHERE addressid = ${stringify(addressid)} AND folder_name = ${stringify(folder_name)} ORDER BY created_at DESC`
                : `SELECT * FROM bookmarks WHERE addressid = ${stringify(addressid)} ORDER BY created_at DESC`;
                
            const results = await this.db!.getAllAsync<BookmarkType>(query);
            return results;
        } catch (error) {
            console.error(`Failed to get bookmarks for user ${addressid}:`, error);
            return [];
        }
    }
    
    async deleteBookmark(addressid: string, postid: string): Promise<void> {
        if (!this.db) await this.init();
        
        try {
            await this.db!.execAsync(`
                DELETE FROM bookmarks WHERE addressid = ${stringify(addressid)} AND postid = ${stringify(postid)}
            `);
        } catch (error) {
            console.error(`Failed to delete bookmark ${addressid} -> ${postid}:`, error);
            throw error;
        }
    }
    
    // Post Hashtag operations
    async savePostHashtags(postid: string, hashtags: string[]): Promise<void> {
        if (!this.db) await this.init();
        if (!hashtags.length) return;
        
        const now = Math.floor(Date.now() / 1000);
        
        try {
            // First delete existing hashtags for this post
            await this.deletePostHashtags(postid);
            
            // Insert new hashtags
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO post_hashtag (postid, tag_name, created_at)
            VALUES
            ${hashtags.map(hashtag => `(
                ${stringify(postid)},
                ${stringify(hashtag.startsWith('#') ? hashtag.substring(1) : hashtag)},
                ${stringify(now)}
            )`).join(',\n')}`);        } catch (error) {
            console.error('Failed to save post hashtags:', error);
            throw error;
        }
    }
    
    async getPostHashtags(postid: string): Promise<string[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<{ tag_name: string }>(`
                SELECT tag_name FROM post_hashtag WHERE postid = ${stringify(postid)} ORDER BY created_at ASC
            `);
            return results.map(row => row.tag_name);
        } catch (error) {
            console.error(`Failed to get hashtags for post ${postid}:`, error);
            return [];
        }
    }
    
    async getPostsByHashtag(hashtag: string): Promise<string[]> {
        if (!this.db) await this.init();
        
        const normalizedHashtag = hashtag.startsWith('#') ? hashtag.substring(1) : hashtag;
        
        try {
            const results = await this.db!.getAllAsync<{ postid: string }>(`
                SELECT postid FROM post_hashtag WHERE tag_name = ${stringify(normalizedHashtag)} ORDER BY created_at DESC
            `);
            return results.map(row => row.postid);
        } catch (error) {
            console.error(`Failed to get posts for hashtag ${hashtag}:`, error);
            return [];
        }
    }
    
    async deletePostHashtags(postid: string): Promise<void> {
        if (!this.db) await this.init();
        
        try {
            await this.db!.execAsync(`
                DELETE FROM post_hashtag WHERE postid = ${stringify(postid)}
            `);
        } catch (error) {
            console.error(`Failed to delete hashtags for post ${postid}:`, error);
            throw error;
        }
    }

    
    // Reports operations
    async saveReports(reports: ReportType[]): Promise<void> {
        if (!this.db) await this.init();
        if (!reports.length) return;
        
        try {
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO reports 
            (addressid, target, reason, description, status, created_at, resolved_at, resolved_by)
            VALUES
            ${reports.map(report => `(
                ${stringify(report.addressid)},
                ${stringify(report.target)},
                ${stringify(report.reason)},
                ${stringify(report.description || '')},
                ${stringify(report.status || 'pending')},
                ${stringify(report.created_at)},
                ${report.resolved_at ? stringify(report.resolved_at) : 'NULL'},
                ${report.resolved_by ? stringify(report.resolved_by) : 'NULL'}
            )`).join(',\n')}`);
        } catch (error) {
            console.error('Failed to save reports:', error);
            throw error;
        }
    }
    
    async getAllReports(): Promise<ReportType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<ReportType>(`
                SELECT * FROM reports ORDER BY created_at DESC
            `);
            return results;
        } catch (error) {
            console.error('Failed to get all reports:', error);
            return [];
        }
    }
    
    async getUserReports(addressid: string): Promise<ReportType[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<ReportType>(`
                SELECT * FROM reports WHERE addressid = ${stringify(addressid)} ORDER BY created_at DESC
            `);
            return results;
        } catch (error) {
            console.error(`Failed to get reports for user ${addressid}:`, error);
            return [];
        }
    }
    
    // Essay Topics operations (Mr. MATE)
    async saveEssayTopics(topics: EssayTopic[]): Promise<void> {
        if (!this.db) await this.init();
        if (!topics.length) return;
        
        try {
            await this.db!.execAsync(`
            INSERT OR REPLACE INTO essay_topics 
            (topicid, hashtags, encouraging_kr, encouraging_id, created_date, created_at)
            VALUES
            ${topics.map(topic => `(
                ${stringify(topic.topicid)},
                ${stringify(JSON.stringify(topic.hashtags))},
                ${stringify(topic.encouraging_kr)},
                ${stringify(topic.encouraging_id)},
                ${stringify(topic.created_date)},
                ${stringify(topic.created_at)}
            )`).join(',\n')}`);
        } catch (error) {
            console.error('Failed to save essay topics:', error);
            throw error;
        }
    }
    
    async getEssayTopics(): Promise<EssayTopic[]> {
        if (!this.db) await this.init();
        
        try {
            const results = await this.db!.getAllAsync<any>(`
                SELECT * FROM essay_topics ORDER BY created_at DESC LIMIT 3
            `);
            
            // Parse hashtags JSON string back to array
            return results.map(row => ({
                ...row,
                hashtags: JSON.parse(row.hashtags)
            }));
        } catch (error) {
            console.error('Failed to get essay topics:', error);
            return [];
        }
    }
    
    /**
     * Release cache resources
     */
    release(): void {
        if (this.db) {
            this.db.closeSync();
            this.db = null;
        }
    }
}

// Export singleton instance
// NOTE: This is a CACHE service, not primary database
export const socialDatabase = new SocialDatabase();